<template>
  <div
    class="product-table__no-products-found"
    v-if="products?.count===0"
  >
    <h4>
      {{ $tv({ messageKey: 'results', messageValues: { total: resultsMessage } }, 'categories') }}
    </h4>
    <div class="product-table__no-products-found__headline" />
    <div>
      <div
        class="product-table__no-products-found__content"
        v-show="showNoProducts && products.count === 0"
      >
        <NoProductsToShow
          class="show_no_products_icon"
        />

        <div
          class="product-table__no-products-found__content__description"
        >
          {{ $tv('showNoProducts', 'categories') }}
        </div>
        <div class="product-table__no-products-found__content__button">
          <ReactiveButton
            :is-primary-button="false"
            @click="handleContactClick"
          >
            <div class="product-table__no-products-found__content__button__item">
              <span>
                {{ $tv('Contact', 'footer') }}
              </span>
              <ArrowDown />
            </div>
          </ReactiveButton>
        </div>
      </div>
      <div
        v-show="showResetFilters && products.count === 0"
        class="product-table__no-products-found__content"
      >
        <ResetFilters
          class="show_no_products_icon"
        />
        <div class="product-table__no-products-found__content__description">
          {{ $tv('noProductsFoundReset', 'categories') }}
        </div>
        <div class="product-table__no-products-found__content__button">
          <ReactiveButton
            :button-text="$tv('reset-filter', 'categories')"
            :is-primary-button="false"
            @click="resetFilters"
          />
        </div>
      </div>
    </div>
    <div
      v-if="showNoProducts"
      class="product-table__no-products-found__contact-form"
    >
      <contact-form
        ref="contactFormRef"
        :form-data="formData"
        @clearFormData="clearFormData"
        class="product-table__no-products-found__contact-form__form"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import NoProductsToShow from '~/assets/icons/no_products_to_show.svg?component'
import ArrowDown from '~/assets/icons/md_arrow_downward.svg?component'
import ResetFilters from '~/assets/icons/no_products_found.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import ContactForm from '~/components/popup/ContactForm'

const i18n = useI18nExtended()

const props = defineProps({
  products: {
    type: Object as () => { count: number },
    required: true
  },
  hasActiveValues: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  resetInProgress: {
    type: Boolean,
    default: false
  }
})
const showNoProducts = ref(!props.hasActiveValues)
const showResetFilters = ref(props.hasActiveValues)
const contactFormRef = ref<null>(null)

const resultsMessage = computed(() => {
  const locale = i18n.locale.value === 'en' ? 'en' : 'de'
  return props.products?.count?.toLocaleString(locale)
})
const emit = defineEmits(['resetFilters', 'clearFormData'])

const handleContactClick = async () => {
  await nextTick()
  if (contactFormRef.value?.$refs?.contactFormHeaderRef) {
    const headerElement = contactFormRef.value.$refs.contactFormHeaderRef
    const elementTop = headerElement.getBoundingClientRect().top + window.scrollY

    const offset = 150

    window.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth'
    })
  }
}

const resetFilters = () => {
  emit('resetFilters')
}

const clearFormData = () => {
  emit('clearFormData')
}

watch(
  () => [props.hasActiveValues, props.resetInProgress],
  ([hasActiveValues, resetInProgress]) => {
    if (resetInProgress) {
      showNoProducts.value = false
      showResetFilters.value = true
    } else {
      showNoProducts.value = !hasActiveValues
      showResetFilters.value = hasActiveValues
    }
  },
  {immediate: true}
)
</script>
<style lang="scss">
.product-table {
  position: relative;
  min-height: 15.625rem;
  padding-top: .5rem;

  &__no-products-found {
    &__headline {
      height: 2.5rem;
      background-color: $color__fst__primary;
      margin: 0 2rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    h4 {
      margin: 0 2rem;
      padding-bottom: .5rem;

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__content {
      display: flex;
      align-items: center;
      flex-direction: column;
      background-color: $color__fst__white;
      margin: 0 2rem;

      .show_no_products_icon {
        padding-top: 1.25rem;
      }

      .icon.icon--svg {
        width: 9.375rem;
        height: 9.375rem;
      }

      &__description {
        padding: 1rem;
      }

      &__button {
        margin-bottom: 1.5rem;

        &__item {
          display: flex;
          gap: .2rem;
          align-content: center;

          span {
            padding-top: .12rem;
          }

          .icon.icon--svg {
            width: 1.5rem;
            height: 1.55rem;
          }
        }
      }

      @include breakpoint-down($md) {
        margin: 0 1rem;
      }
    }

    &__contact-form {
      display: flex;
      justify-content: center;
      padding: 3rem;

      &__form {
        max-width: 40rem;
      }
    }
  }
}
</style>
